import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ClipboardCheck, X, Trash2, Mail } from "lucide-react";

const STATUS_LABELS = {
  new: "Νέο",
  in_review: "Σε αξιολόγηση",
  matched: "Ταιριάστηκε",
  closed: "Κλείσιμο",
};

export default function AdminQuestionnairesPanel({ L }) {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/detailed-questionnaires");
      setRows(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/detailed-questionnaires/${id}`);
      setDetail(data);
    } catch { /* ignore */ }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/detailed-questionnaires/${id}`, { status });
      await load();
      if (detail?.id === id) setDetail({ ...detail, status });
    } catch { /* ignore */ }
  };

  const del = async (id) => {
    if (!window.confirm(L ? "Διαγραφή ερωτηματολογίου;" : "Delete questionnaire?")) return;
    await api.delete(`/admin/detailed-questionnaires/${id}`);
    setDetail(null);
    await load();
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(L ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
  };

  return (
    <div data-testid="admin-questionnaires-panel">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardCheck className="w-5 h-5 text-slate-500" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Ερωτηματολόγια Αναγκών" : "Needs questionnaires"}</div>
          <h3 className="font-heading text-lg font-bold text-slate-900">{rows.length} {L ? "αιτήσεις" : "submissions"}</h3>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">{L ? "Φόρτωση..." : "Loading..."}</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <ClipboardCheck className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <div className="text-sm">{L ? "Δεν υπάρχουν αιτήσεις ακόμη" : "No submissions yet"}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="questionnaires-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Ημ/νία", "Επιχείρηση", "Υπεύθυνος", "Κλάδος", "Αγορές", "Status", ""].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => openDetail(r.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`questionnaire-row-${r.id}`}>
                    <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">{fmt(r.created_at)}</td>
                    <td className="py-3 px-4 font-semibold text-slate-900">{r.company_name}</td>
                    <td className="py-3 px-4 text-slate-700">{r.contact_person}<div className="text-[11px] text-slate-500">{r.contact_email}</div></td>
                    <td className="py-3 px-4 text-slate-600 text-xs">{r.industry}</td>
                    <td className="py-3 px-4 text-slate-600 text-xs max-w-xs truncate">{r.target_regions}</td>
                    <td className="py-3 px-4"><StatusPill status={r.status} /></td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={(e) => { e.stopPropagation(); del(r.id); }} className="p-1.5 text-slate-400 hover:text-red-600" title="Διαγραφή">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full my-8 border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="questionnaire-detail-modal">
            <header className="sticky top-0 bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between rounded-t-xl">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Ερωτηματολόγιο</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-0.5">{detail.company_name}</div>
                <div className="text-xs text-slate-500 mt-1">{detail.contact_person} · {detail.contact_email}</div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`mailto:${detail.contact_email}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Απάντηση"><Mail className="w-4 h-4" /></a>
                <button onClick={() => setDetail(null)} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
              </div>
            </header>

            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap bg-slate-50/50">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status:</span>
              {Object.entries(STATUS_LABELS).map(([k, label]) => (
                <button key={k} onClick={() => updateStatus(detail.id, k)}
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${detail.status === k ? "bg-[#0052CC] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5 text-sm">
              <Section title="Επιχείρηση">
                <Kv label="Ιστοσελίδα" v={detail.website} />
                <Kv label="Τηλέφωνο" v={detail.contact_phone} />
                <Kv label="Κλάδος" v={detail.industry} />
                <Kv label="Προϊόντα προς προώθηση" v={detail.products_to_promote} />
                <Kv label="Περιγραφή" v={detail.products_description} />
              </Section>
              <Section title="Αγορές">
                <Kv label="Ενδιαφέρον" v={detail.target_regions} />
                <Kv label="Προτεραιότητες" v={detail.priority_countries} />
                <Kv label="Παρουσία ήδη" v={detail.has_existing_presence ? "Ναι" : "Όχι"} />
                <Kv label="Παρουσία σε" v={detail.existing_presence_countries} />
                <Kv label="Εξαιρέσεις" v={detail.excluded_countries} />
              </Section>
              <Section title="Προφίλ Συνεργάτη">
                <Kv label="Κατοικία" v={detail.partner_residence_requirement} />
                <Kv label="Εμπειρία κλάδου" v={detail.industry_experience_requirement} />
                <Kv label="Κλάδος" v={detail.required_industry} />
                <Kv label="Υποκλάδος" v={detail.required_sub_industry} />
                <Kv label="Ελάχιστη εμπειρία" v={detail.min_experience_years} />
                <Kv label="Δεξιότητες" v={detail.sales_skills} />
                <Kv label="Άλλες δεξιότητες" v={detail.sales_skills_other} />
              </Section>
              <Section title="Πελάτες">
                <Kv label="Τύπος πελατών" v={detail.customer_types} />
                <Kv label="Άλλος τύπος" v={detail.customer_types_other} />
              </Section>
              <Section title="Γλώσσες & Σπουδές">
                <Kv label="Γλώσσες" v={detail.languages} />
                <Kv label="Επίπεδο" v={detail.language_level} />
                <Kv label="Πτυχίο" v={detail.degree_requirement} />
                <Kv label="Αντικείμενο" v={detail.preferred_field_of_study} />
              </Section>
              <Section title="Τοπικό Δίκτυο">
                <Kv label="Απαιτήσεις" v={detail.local_knowledge} />
              </Section>
              <Section title="Ευθύνες Συνεργάτη">
                <Kv label="Ενέργειες" v={detail.partner_responsibilities} />
              </Section>
              <Section title="Ιδανικός Πελάτης & Εμπορικοί Όροι">
                <Kv label="Επιθυμητό αποτέλεσμα" v={detail.desired_outcomes} />
                <Kv label="Άλλο αποτέλεσμα" v={detail.desired_outcomes_other} />
                <Kv label="Περιγραφή ιδανικού" v={detail.ideal_customer_description} />
                <Kv label="Μέγεθος" v={detail.ideal_customer_size} />
                <Kv label="Θέση επικοινωνίας" v={detail.ideal_contact_position} />
                <Kv label="Εξαγωγική ετοιμότητα" v={detail.export_readiness} />
                <Kv label="Τιμή χονδρικής" v={detail.wholesale_price} />
                <Kv label="Ελάχιστη παραγγελία" v={detail.minimum_order} />
                <Kv label="Samples" v={detail.can_send_samples == null ? null : (detail.can_send_samples ? "Ναι" : "Όχι")} />
                <Kv label="Αποκλειστική αντιπροσώπευση" v={detail.exclusive_representation} />
                <Kv label="Private label" v={detail.private_label_option == null ? null : (detail.private_label_option ? "Ναι" : "Όχι")} />
                <Kv label="Χρονοδιάγραμμα" v={detail.start_horizon} />
                <Kv label="Προϋπολογισμός" v={detail.budget_range} />
                <Kv label="Πληρωμή ανά ώρα" v={detail.hourly_rate_offered} />
                <Kv label="Μοντέλο αμοιβής" v={detail.payment_model} />
              </Section>
              <Section title="Υπηρεσίες">
                <Kv label="Έρευνα αγοράς" v={detail.services_market_research} />
                <Kv label="Πελατολόγιο" v={detail.services_client_database} />
                <Kv label="Τηλεφωνικές" v={detail.services_phone_sales} />
                <Kv label="Email outreach" v={detail.services_email_outreach} />
                <Kv label="LinkedIn" v={detail.services_linkedin} />
                <Kv label="Συναντήσεις" v={detail.services_meetings} />
                <Kv label="Τοπική παρουσία" v={detail.services_local_presence} />
                <Kv label="Distributors" v={detail.services_distributors} />
                <Kv label="Εκθέσεις" v={detail.services_trade_shows} />
                <Kv label="Υλικό πωλήσεων" v={detail.services_sales_materials} />
                <Kv label="Ψηφιακή παρουσία" v={detail.services_digital_presence} />
                <Kv label="Διαφήμιση" v={detail.services_advertising} />
                <Kv label="CRM" v={detail.services_crm} />
                <Kv label="Reporting" v={detail.services_reporting} />
                <Kv label="Συνεχής ανάπτυξη" v={detail.services_continuous_dev} />
                <Kv label="Ενδιαφέρον" v={detail.services_most_interested} />
              </Section>
              {detail.business_goal_description && (
                <Section title="Στόχος πελάτη">
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-slate-700 leading-relaxed whitespace-pre-wrap text-sm">
                    {detail.business_goal_description}
                  </div>
                </Section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    new: "bg-emerald-100 text-emerald-700",
    in_review: "bg-amber-100 text-amber-700",
    matched: "bg-[#0052CC]/10 text-[#0052CC]",
    closed: "bg-slate-100 text-slate-500",
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${colors[status] || "bg-slate-100"}`}>{STATUS_LABELS[status] || status}</span>;
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Kv({ label, v }) {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="grid grid-cols-3 gap-3 border border-slate-100 rounded px-3 py-2">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="col-span-2 text-sm text-slate-800">
        {Array.isArray(v) ? (
          <ul className="list-disc list-inside space-y-0.5">{v.map((it, i) => <li key={i}>{it}</li>)}</ul>
        ) : (
          <span className="whitespace-pre-wrap">{v}</span>
        )}
      </div>
    </div>
  );
}
